import { faExclamation } from '@fortawesome/free-solid-svg-icons/faExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, FlexGrid, Header, Link, Loader, Message, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { capitalize } from '../../../lib/utils';
import { getNewsroomFormattedDate } from '../../../lib/utils/newsroom';
import { SUPPORTED_LANGUAGES } from '../../constants';
import Breadcrumb from '../Breadcrumb';
import Page from '../Page';
import styles from './PublicationsPage.module.css';

export const TYPES = ['article', 'press release', 'publication'];

const cx = classnames.bind(styles);

function PublicationsPage({ error, loading, pubType, publications, ...rest }) {
  const { t, i18n } = useTranslation(['article', 'miscellaneous']);

  const publicationsByYear = useMemo(() => publications.reduce((map, publication) => {
    const year = new Date(publication.date.replace(' ', 'T')).getFullYear();
    const existing = map[year] || [];

    return {
      ...map,
      [year]: [...existing, publication],
    };
  }, {}), [publications]);

  const pascalPluralPubType = `${capitalize(pubType, true).replace(' ', '')}s`;

  return (
    <Page compact {...rest}>
      <Container className={cx('container')}>
        <div className={cx('breadcrumbs')}>
          <Breadcrumb linkTo="/" showSeparator={false}>
            {t('navigation.home', { ns: 'miscellaneous' })}
          </Breadcrumb>
          <Breadcrumb linkTo="/newsroom">
            {t('newsroom', { ns: 'article' })}
          </Breadcrumb>
        </div>
        <Header as="h1" className={cx('publicationsHeader')}>
          {t(`gasBuddy${pascalPluralPubType}`, { ns: 'article' })}
        </Header>
        {loading && (
          <Loader />
        )}
        {!!error && !publications.length && (
          <Text as="p">
            {t('errors.generic', { ns: 'miscellaneous' })}
          </Text>
        )}
        {!error && !loading && !publications.length && (
          <Text as="p">
            {t(`unableToFind${pascalPluralPubType}`, { ns: 'article' })}
            <br />
            <Trans i18nKey="article:goBackToNewsroom">
              Please <Link to="/newsroom">click here</Link> to go back to the Newsroom.
            </Trans>
          </Text>
        )}
        {!error && !loading && publications.length && i18n.language === SUPPORTED_LANGUAGES.FRENCH && (
          <Message color="gold" icon={<FontAwesomeIcon icon={faExclamation} />}>
            {t(pubType === 'article' ? 'thirdPartyArticles' : 'missingTranslations', { ns: 'article' })}
          </Message>
        )}
        {!!publications.length && (
          <FlexGrid>
            {Object.keys(publicationsByYear).sort((a, b) => b - a).map(year => (
              <FlexGrid.Column key={year} tablet={6}>
                <Header as="h2">{year}</Header>
                {publicationsByYear[year]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map(publication => (
                    <div key={`${publication.headline}_${publication.date}_${publication.legacyId}`} className={cx('publication')}>
                      {publication.source && (
                        <Header as="h3">
                          {publication.source}
                        </Header>
                      )}
                      <Link href={publication.headlineLink}>
                        {publication.headline}
                      </Link>
                      <Text as="p">
                        {getNewsroomFormattedDate(publication.date, i18n.language)}
                      </Text>
                    </div>
                  ))
                }
              </FlexGrid.Column>
            ))}
          </FlexGrid>
        )}
      </Container>
    </Page>
  );
}

PublicationsPage.propTypes = {
  error: PropTypes.bool,
  loading: PropTypes.bool,
  pubType: PropTypes.oneOf(TYPES),
  publications: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    headlineLink: PropTypes.string.isRequired,
    legacyId: PropTypes.string,
    source: PropTypes.string,
  })),
};

PublicationsPage.defaultProps = {
  error: false,
  loading: false,
  pubType: 'publication',
  publications: [],
};

export default PublicationsPage;
